/**
 * Confirmation Banner Component
 *
 * Displays confirmation interrupts (e.g., "Save rules to library?")
 * with accept/reject buttons. Follows the same pattern as note-ai-edit.
 */

import React from 'react';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  type RuleBuildChatState,
} from '../store';

export interface ConfirmationBannerProps {
  /** Callback when user confirms (e.g., saves rules) */
  onConfirm: () => void;
  /** Callback when user cancels/rejects */
  onCancel: () => void;
  /** Whether the action is being processed */
  isProcessing?: boolean;
}

export const ConfirmationBanner: React.FC<ConfirmationBannerProps> = ({
  onConfirm,
  onCancel,
  isProcessing = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Get interrupt state from Redux
  const chatState = useAppSelector(
    (state) => (state as unknown as { 'ruleBuild/chat'?: RuleBuildChatState })['ruleBuild/chat']
  );

  const interrupt = chatState?.interrupt;

  // Don't render if no active interrupt or not a CONFIRMATION type
  if (!interrupt?.isActive || interrupt.eventType !== 'CONFIRMATION') {
    return null;
  }

  // Handle dismiss (reject)
  const handleDismiss = () => {
    onCancel();
  };

  // Handle confirm
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        px: 2,
        pr: 5,
        py: 2,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        flexShrink: 0,
      }}
    >
      {/* Dismiss Button */}
      <IconButton
        size="small"
        onClick={handleDismiss}
        disabled={isProcessing}
        aria-label="Dismiss"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Message */}
      {interrupt.message && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <SaveIcon
            sx={{
              fontSize: 20,
              color: theme.palette.primary.main,
              mt: 0.25,
            }}
          />
          <Box sx={{ flex: 1, pr: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                whiteSpace: 'pre-line',
                color: 'textColors.darkText',
              }}
            >
              {interrupt.message}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleDismiss}
          disabled={isProcessing}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
          }}
        >
          {interrupt.cancelLabel || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleConfirm}
          disabled={isProcessing}
          startIcon={isProcessing ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            bgcolor: '#22C55E',
            '&:hover': {
              bgcolor: '#16A34A',
            },
          }}
        >
          {isProcessing ? 'Saving...' : interrupt.confirmLabel || 'Confirm'}
        </Button>
      </Box>
    </Box>
  );
};

export default ConfirmationBanner;
